import { marked, Token, Tokens, TokensList } from "marked";
import { postprocessHtml, preprocessMarkdown } from "PFCore/helpers/markdown";

type TruncateMarkdownOptions = {
  emojify?: boolean;
};

type Accumulator = {
  tokens: Token[];
  truncated: boolean;
  done: boolean;
};

type InlineAccumulator = {
  remaining: number;
} & Accumulator;

type BlockAccumulator = {
  budget: number;
} & Accumulator;

const ELLIPSIS = "...";
const WHITESPACE_TOKENS = new Set(["space", "softbreak", "br"]);

export const visibleTextLength = (token: Token): number => {
  if (!token) {
    return 0;
  }

  if (WHITESPACE_TOKENS.has(token.type) || token.type === "hr") {
    return 0;
  }

  if (token.type === "text" || token.type === "codespan" || token.type === "escape") {
    return ((token as Tokens.Text).text ?? "").length;
  }

  const withTokens = token as { tokens?: Token[] };
  if (withTokens.tokens && withTokens.tokens.length > 0) {
    return withTokens.tokens.reduce((sum, t) => sum + visibleTextLength(t), 0);
  }

  const withItems = token as { items?: Token[] };
  if (withItems.items && withItems.items.length > 0) {
    return withItems.items.reduce((sum, item) => sum + visibleTextLength(item), 0);
  }

  return ((token as Tokens.Text).text ?? token.raw ?? "").length;
};

const truncateAtWordBoundary = (text: string, budget: number): string => {
  const slice = text.slice(0, budget);
  const lastSpaceIndex = slice.lastIndexOf(" ");
  const cutIndex = lastSpaceIndex > 0 ? lastSpaceIndex : slice.length;

  return slice.slice(0, cutIndex).trimEnd();
};

const addInlineToken = (acc: InlineAccumulator, token: Token): InlineAccumulator => {
  if (acc.done) {
    return acc;
  }

  if (WHITESPACE_TOKENS.has(token.type)) {
    return { ...acc, tokens: [...acc.tokens, token] };
  }

  if (token.type === "text") {
    const { text } = token as Tokens.Text;

    if (text.length <= acc.remaining) {
      return { ...acc, tokens: [...acc.tokens, token], remaining: acc.remaining - text.length };
    }

    const truncatedText = truncateAtWordBoundary(text, acc.remaining);

    return {
      tokens: [...acc.tokens, { ...token, text: truncatedText, raw: truncatedText } as Tokens.Text],
      remaining: 0,
      truncated: true,
      done: true
    };
  }

  const visibleLength = visibleTextLength(token);

  if (visibleLength <= acc.remaining) {
    return { ...acc, tokens: [...acc.tokens, token], remaining: acc.remaining - visibleLength };
  }

  return { ...acc, truncated: true, done: true };
};

const sliceInlineTokens = (
  inlineTokens: Token[],
  budget: number
): { tokens: Token[]; truncated: boolean } => {
  const initial: InlineAccumulator = { tokens: [], remaining: budget, truncated: false, done: false };
  const { tokens, truncated } = inlineTokens.reduce(addInlineToken, initial);

  return { tokens, truncated };
};

const makeParagraphToken = (inlineTokens: Token[]): Tokens.Paragraph => {
  const rawText = inlineTokens.map((token) => (token as Tokens.Text).text ?? token.raw ?? "").join("");

  return { type: "paragraph", raw: rawText, text: rawText, tokens: inlineTokens } as Tokens.Paragraph;
};

const addBlockToken = (acc: BlockAccumulator, token: Token): BlockAccumulator => {
  if (acc.done || acc.budget <= 0) {
    return { ...acc, truncated: true, done: true };
  }

  const visibleLength = visibleTextLength(token);

  if (visibleLength <= acc.budget) {
    return { ...acc, tokens: [...acc.tokens, token], budget: acc.budget - visibleLength };
  }

  if (token.type === "paragraph") {
    const { tokens: slicedInline, truncated } = sliceInlineTokens(
      (token as Tokens.Paragraph).tokens ?? [],
      acc.budget
    );
    const nextTokens =
      slicedInline.length > 0 ? [...acc.tokens, makeParagraphToken(slicedInline)] : acc.tokens;

    return { tokens: nextTokens, budget: 0, truncated, done: true };
  }

  return { ...acc, truncated: true, done: true };
};

export const visibleMarkdownLength = (raw: string, emojify = true): number => {
  const text = preprocessMarkdown(raw, emojify);
  const blockTokens = marked.lexer(text);

  return blockTokens.reduce((sum, token) => sum + visibleTextLength(token), 0);
};

/**
 * Truncate raw markdown/HTML at token boundaries using the marked.js lexer.
 *
 * Block tokens are included whole or skipped entirely. For an overflowing
 * paragraph, inline tokens are accumulated with word-boundary fallback.
 * Returns fully-rendered HTML with the same emojify + XSS pipeline as `fullMarkdown`.
 *
 * When truncation occurs, "..." is appended after the final rendered HTML block.
 */
export const truncateMarkdown = (
  raw: string | undefined,
  charLimit: number,
  options: TruncateMarkdownOptions = {}
): string => {
  if (!raw) {
    return "";
  }

  const { emojify = true } = options;
  const text = preprocessMarkdown(raw, emojify);
  const blockTokens = marked.lexer(text);
  const totalVisibleLength = blockTokens.reduce((sum, token) => sum + visibleTextLength(token), 0);

  if (totalVisibleLength <= charLimit) {
    return postprocessHtml(marked.parse(text) as string, emojify);
  }

  const initial: BlockAccumulator = {
    tokens: [],
    budget: charLimit,
    truncated: false,
    done: false
  };
  const { tokens: resultTokens } = blockTokens.reduce(addBlockToken, initial);

  const tokensList = resultTokens as TokensList;
  tokensList.links = (blockTokens as TokensList).links ?? {};

  const html = marked.parser(tokensList);

  return postprocessHtml(html, emojify) + ELLIPSIS;
};
